/**
 * Society finances (accounting lite) — the transparency report committees get
 * elected promising. Three tiers on one screen:
 *   every member   monthly collected-vs-spent statement + category breakdown
 *   money.expenses readers (committee/office bearers — incl. every MC
 *                  member's LOCKED read under bye-law 137)  itemized ledger
 *   writers (Hon. Treasurer / admins)  record expenses w/ category, receipt
 *                  photo, and void-with-audit for mistakes
 * Wired: GET /api/payments/statement · /expenses · /expenses/<id>,
 * POST /api/payments/expenses · /expenses/<id>/void.
 */
import React from 'react';
import { Image, View } from 'react-native';

import { formatINR, radius, space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { PhotoAttach } from '@/components/photo/PhotoAttach';
import { useT, monthLong, monthShort } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, Chip, EmptyState, Field, Hairline, Input, Pill,
  Screen, SectionLabel, Sheet, Skeleton, TopBar,
} from '@/ui';

const CATEGORIES = ['MAINTENANCE', 'REPAIRS', 'SECURITY', 'UTILITIES', 'HOUSEKEEPING', 'EVENTS', 'ADMIN', 'OTHER'] as const;
const CAT_LABEL: Record<string, DictKey> = {
  MAINTENANCE: 'fin_cat_maintenance', REPAIRS: 'fin_cat_repairs', SECURITY: 'fin_cat_security', UTILITIES: 'fin_cat_utilities',
  HOUSEKEEPING: 'fin_cat_housekeeping', EVENTS: 'fin_cat_events', ADMIN: 'fin_cat_admin', OTHER: 'fin_cat_other',
};

type MonthRow = { month: string; collected: number; spent: number; net: number; byCategory: Record<string, number> };
type ExpenseRow = {
  id: number; category: string; amount: number; note: string; expenseDate: string;
  recordedByName: string | null; hasReceipt: boolean;
};

function monthLabel(key: string): string {
  const d = new Date(`${key}-01T00:00:00`);
  return `${monthLong(d.getMonth())} ${d.getFullYear()}`;
}

function inrP(paise: number): string {
  return formatINR(paise / 100);
}

export default function FinancesScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [months, setMonths] = React.useState<MonthRow[]>([]);
  const [bits, setBits] = React.useState<string[]>([]);
  const [ledger, setLedger] = React.useState<ExpenseRow[] | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [notice, setNotice] = React.useState<string | null>(null);

  // record sheet
  const [recordOpen, setRecordOpen] = React.useState(false);
  const [category, setCategory] = React.useState<string>('MAINTENANCE');
  const [amount, setAmount] = React.useState('');
  const [note, setNote] = React.useState('');
  const [date, setDate] = React.useState('');
  const [receipt, setReceipt] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  // receipt viewer
  const [viewReceipt, setViewReceipt] = React.useState<string | null>(null);

  const canRead = bits.includes('read');
  const canWrite = bits.includes('write');

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [st, me] = await Promise.all([
        api.get<{ months: MonthRow[] }>('/api/payments/statement?months=6'),
        api.get<{ perms?: Record<string, string[]> }>('/api/positions/me').catch(() => ({ perms: {} as Record<string, string[]> })),
      ]);
      setMonths(st.months ?? []);
      const b = me.perms?.['money.expenses'] ?? [];
      setBits(b);
      if (b.includes('read')) {
        const lg = await api.get<{ items: ExpenseRow[] }>('/api/payments/expenses');
        setLedger(lg.items ?? []);
      }
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const doRecord = () => {
    if (busy) return;
    const rupees = Number(amount.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(rupees) || rupees < 1) { setNotice(t('fin_err_amount')); return; }
    if (note.trim().length < 3) { setNotice(t('fin_err_note')); return; }
    if (date.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) { setNotice(t('fin_err_date')); return; }
    setBusy(true);
    api.post<unknown>('/api/payments/expenses', {
      category, amount: Math.round(rupees * 100), note: note.trim(),
      expenseDate: date.trim() || undefined, receiptPhoto: receipt ?? undefined,
    })
      .then(() => { setNotice(t('fin_ok_recorded', { amount: inrP(Math.round(rupees * 100)), cat: t(CAT_LABEL[category] ?? 'fin_cat_other') })); void load(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? t('fin_err_record')))
      .finally(() => { setBusy(false); setRecordOpen(false); setAmount(''); setNote(''); setReceipt(null); });
  };

  const doVoid = (row: ExpenseRow) => {
    api.post<unknown>(`/api/payments/expenses/${row.id}/void`, { reason: 'Voided from the ledger' })
      .then(() => { setNotice(t('fin_ok_voided', { amount: inrP(row.amount) })); void load(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? t('fin_err_void')));
  };

  const openReceipt = (row: ExpenseRow) => {
    api.get<{ expense: { receiptPhoto?: string | null } }>(`/api/payments/expenses/${row.id}`)
      .then((b) => { if (b.expense.receiptPhoto) setViewReceipt(b.expense.receiptPhoto); })
      .catch(() => setNotice(t('fin_err_receipt')));
  };

  const maxFlow = Math.max(1, ...months.map((m) => Math.max(m.collected, m.spent)));

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('fin_title')}
        onBack={goBack}
        right={canWrite ? <SmallButton label={t('fin_expense_btn')} icon="plus" onPress={() => {
          setCategory('MAINTENANCE'); setAmount(''); setNote(''); setDate(''); setReceipt(null);
          setNotice(null); setRecordOpen(true);
        }} /> : undefined}
      />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={96} />)}</View>
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
            title={t('fin_err_load')}
            body={t('ct_check_connection')}
            cta={t('retry')}
            onCta={() => { void load(); }}
          />
        ) : (
          <>
            <AppText variant="bodySm" tone="tertiary">
              {t('fin_intro')}
            </AppText>
            {notice ? (
              <AppText variant="bodySm" weight="500" style={{ marginTop: space.s3, color: colors.primary700 }}>
                {notice}
              </AppText>
            ) : null}

            <SectionLabel style={{ marginTop: space.s4 }}>{t('fin_monthly_statement')}</SectionLabel>
            <View style={{ gap: space.s3 }}>
              {months.map((m) => (
                <Card key={m.month}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <AppText variant="bodyMd" weight="600" style={{ flex: 1 }}>{monthLabel(m.month)}</AppText>
                    <AppText variant="bodyMd" weight="600" mono
                      style={{ color: m.net >= 0 ? colors.primary700 : colors.danger500 }}>
                      {m.net >= 0 ? '+' : '−'}{inrP(Math.abs(m.net))}
                    </AppText>
                  </View>
                  {([['fin_collected', m.collected, colors.primary500], ['fin_spent', m.spent, colors.accent500]] as const).map(([labelKey, value, color]) => (
                    <View key={labelKey} style={{ marginTop: space.s2 }}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <AppText variant="bodySm" tone="tertiary">{t(labelKey)}</AppText>
                        <AppText variant="bodySm" mono>{inrP(value)}</AppText>
                      </View>
                      <View style={{ height: 5, borderRadius: 3, backgroundColor: colors.canvas1, marginTop: 3, overflow: 'hidden' }}>
                        <View style={{ height: '100%', borderRadius: 3, width: `${Math.round((value / maxFlow) * 100)}%`, backgroundColor: color }} />
                      </View>
                    </View>
                  ))}
                  {Object.keys(m.byCategory).length > 0 ? (
                    <View style={{ marginTop: space.s3 }}>
                      {Object.entries(m.byCategory).sort((a, b) => b[1] - a[1]).map(([cat, val]) => (
                        <View key={cat} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 }}>
                          <AppText variant="bodySm" tone="secondary">{CAT_LABEL[cat] ? t(CAT_LABEL[cat]) : cat}</AppText>
                          <AppText variant="bodySm" mono tone="secondary">{inrP(val)}</AppText>
                        </View>
                      ))}
                    </View>
                  ) : null}
                </Card>
              ))}
            </View>

            {canRead && ledger ? (
              <>
                <SectionLabel style={{ marginTop: space.s5 }}>{t('fin_ledger_title')}</SectionLabel>
                {ledger.length === 0 ? (
                  <AppText variant="bodySm" tone="tertiary">{t('fin_no_expenses')}</AppText>
                ) : (
                  <Card>
                    {ledger.map((r, i) => (
                      <View key={r.id}>
                        {i > 0 ? <Hairline /> : null}
                        <View style={{ paddingVertical: space.s2 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                            <Pill status="pending" label={(CAT_LABEL[r.category] ? t(CAT_LABEL[r.category]) : r.category).toUpperCase()} />
                            <View style={{ flex: 1 }} />
                            <AppText variant="bodyMd" weight="600" mono>{inrP(r.amount)}</AppText>
                          </View>
                          <AppText variant="bodyMd" style={{ marginTop: 4 }}>{r.note}</AppText>
                          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
                            <AppText variant="bodySm" tone="tertiary" style={{ flex: 1 }}>
                              {(() => { const d = new Date(r.expenseDate); return `${d.getDate()} ${monthShort(d.getMonth())}`; })()}
                              {r.recordedByName ? ` · ${r.recordedByName}` : ''}
                            </AppText>
                            {r.hasReceipt ? (
                              <AppText variant="bodySm" weight="600" tone="brand" onPress={() => openReceipt(r)}>
                                {t('fin_receipt')}
                              </AppText>
                            ) : null}
                            {canWrite ? (
                              <AppText variant="bodySm" weight="500" style={{ color: colors.danger500, marginLeft: space.s3 }}
                                onPress={() => doVoid(r)}>
                                {t('fin_void')}
                              </AppText>
                            ) : null}
                          </View>
                        </View>
                      </View>
                    ))}
                  </Card>
                )}
              </>
            ) : null}
            <View style={{ height: space.s6 }} />
          </>
        )}
      </Screen>

      {/* record expense */}
      <Sheet visible={recordOpen} onClose={() => setRecordOpen(false)}>
        <SectionLabel>{t('fin_record_expense')}</SectionLabel>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginTop: space.s3 }}>
          {CATEGORIES.map((c) => (
            <Chip key={c} label={t(CAT_LABEL[c])} active={category === c} onPress={() => setCategory(c)} />
          ))}
        </View>
        <View style={{ flexDirection: 'row', gap: space.s3, marginTop: space.s4 }}>
          <View style={{ flex: 1 }}>
            <Field label={t('fin_f_amount')}>
              <Input value={amount} onChangeText={setAmount} placeholder={t('fin_ph_amount')} keyboardType="numeric" />
            </Field>
          </View>
          <View style={{ flex: 1.2 }}>
            <Field label={t('fin_f_date')} help={t('fin_f_date_help')}>
              <Input value={date} onChangeText={setDate} placeholder="2026-07-18" />
            </Field>
          </View>
        </View>
        <View style={{ marginTop: space.s3 }}>
          <Field label={t('fin_f_what')}>
            <Input value={note} onChangeText={setNote} placeholder={t('fin_ph_what')} maxLength={250} />
          </Field>
        </View>
        <View style={{ marginTop: space.s3 }}>
          <Field label={t('fin_f_receipt')} help={t('fin_f_receipt_help')}>
            <PhotoAttach value={receipt} onChange={setReceipt} />
          </Field>
        </View>
        <Button title={busy ? t('fin_recording') : t('fin_record_expense_btn')} disabled={busy}
          style={{ marginTop: space.s4 }} onPress={doRecord} />
      </Sheet>

      {/* receipt viewer */}
      <Sheet visible={viewReceipt !== null} onClose={() => setViewReceipt(null)}>
        {viewReceipt ? (
          <Image
            source={{ uri: viewReceipt }}
            resizeMode="contain"
            style={{ width: '100%', height: 380, borderRadius: radius.lg, backgroundColor: colors.canvas1 }}
          />
        ) : null}
      </Sheet>
    </Screen>
  );
}
